import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

type ProofAccent = 'blue' | 'saffron';

interface ProofStat {
  value: string;
  unit: string;
  label: string;
}

interface ProofCard {
  icon: ReactNode;
  title: string;
  body: string;
  bodyEmphasis: string;
  proofFooter: string;
  accent: ProofAccent;
}

const proofStyles = `
  .sv-proof {
    --sv-bg: #ffffff;
    --sv-bg-soft: #F7F8FB;
    --sv-ink: #0A0A0B;
    --sv-ink-soft: #2C2C2E;
    --sv-muted: #6A6A70;
    --sv-line: #EAEAEE;
    --sv-line-soft: #F1F1F4;
    --sv-blue: #1E40F0;
    --sv-blue-soft: #F0F3FE;
    --sv-saffron: #D85A30;
    --sv-saffron-soft: #FBF0EA;
    --sv-radius: 16px;
    --sv-radius-sm: 10px;
    --sv-radius-pill: 999px;

    max-width: 1240px;
    margin: 0 auto;
    padding: 72px 32px 80px;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    color: var(--sv-ink);
    font-size: 15px;
    line-height: 1.6;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  .sv-proof *, .sv-proof *::before, .sv-proof *::after {
    box-sizing: border-box;
  }

  .sv-proof-header {
    text-align: center;
    max-width: 720px;
    margin: 0 auto 44px;
  }

  .sv-proof-eyebrow {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    font-weight: 500;
    color: var(--sv-blue);
    background: var(--sv-blue-soft);
    padding: 6px 14px;
    border-radius: var(--sv-radius-pill);
    margin-bottom: 20px;
    letter-spacing: 0.3px;
  }

  .sv-proof-eyebrow::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--sv-blue);
  }

  .sv-proof-header h2 {
    font-weight: 700;
    font-size: clamp(28px, 3.5vw, 40px);
    line-height: 1.12;
    letter-spacing: 0;
    margin: 0 0 14px;
    color: var(--sv-ink);
  }

  .sv-proof-header p {
    font-size: 16px;
    color: var(--sv-muted);
    margin: 0;
    line-height: 1.55;
  }

  .sv-proof-stats {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 0;
    background: var(--sv-bg);
    border: 1px solid var(--sv-line);
    border-radius: var(--sv-radius);
    overflow: hidden;
    margin-bottom: 32px;
  }

  .sv-proof-stat {
    padding: 26px 32px;
    border-right: 1px solid var(--sv-line-soft);
    text-align: left;
  }

  .sv-proof-stat:last-child {
    border-right: none;
  }

  .sv-proof-stat-value {
    font-size: 36px;
    font-weight: 700;
    line-height: 1;
    color: var(--sv-ink);
    letter-spacing: 0;
    margin: 0 0 8px;
  }

  .sv-proof-stat-unit {
    font-size: 18px;
    color: var(--sv-muted);
    font-weight: 500;
    margin-left: 2px;
    letter-spacing: 0;
  }

  .sv-proof-stat-label {
    font-size: 13px;
    color: var(--sv-muted);
    margin: 0;
    line-height: 1.4;
  }

  .sv-proof-cards {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
  }

  .sv-proof-card {
    background: var(--sv-bg);
    border: 1px solid var(--sv-line);
    border-radius: var(--sv-radius);
    padding: 32px;
    display: flex;
    flex-direction: column;
    transition: transform 0.25s, box-shadow 0.25s, border-color 0.25s;
  }

  .sv-proof-card:hover {
    border-color: #DADAE0;
    box-shadow: 0 8px 24px -8px rgba(10, 10, 11, 0.1);
    transform: translateY(-2px);
  }

  .sv-proof-icon {
    width: 44px;
    height: 44px;
    border-radius: var(--sv-radius-sm);
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 24px;
  }

  .sv-proof-icon svg {
    width: 22px;
    height: 22px;
  }

  .sv-proof-icon[data-accent="blue"] {
    background: var(--sv-blue-soft);
    color: var(--sv-blue);
  }

  .sv-proof-icon[data-accent="saffron"] {
    background: var(--sv-saffron-soft);
    color: var(--sv-saffron);
  }

  .sv-proof-card h3 {
    font-size: 19px;
    font-weight: 600;
    letter-spacing: 0;
    margin: 0 0 10px;
    color: var(--sv-ink);
  }

  .sv-proof-body {
    font-size: 14.5px;
    color: var(--sv-muted);
    line-height: 1.6;
    margin: 0 0 20px;
    flex-grow: 1;
  }

  .sv-proof-body strong {
    color: var(--sv-ink-soft);
    font-weight: 600;
  }

  .sv-proof-card-footer {
    display: flex;
    align-items: center;
    gap: 8px;
    padding-top: 16px;
    border-top: 1px solid var(--sv-line-soft);
    font-size: 12.5px;
    color: var(--sv-muted);
    font-weight: 500;
  }

  .sv-proof-card-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    flex-shrink: 0;
  }

  .sv-proof-card-dot[data-accent="blue"] {
    background: var(--sv-blue);
  }

  .sv-proof-card-dot[data-accent="saffron"] {
    background: var(--sv-saffron);
  }

  @media (max-width: 900px) {
    .sv-proof-stats {
      grid-template-columns: 1fr;
    }

    .sv-proof-stat {
      border-right: none;
      border-bottom: 1px solid var(--sv-line-soft);
    }

    .sv-proof-stat:last-child {
      border-bottom: none;
    }

    .sv-proof-cards {
      grid-template-columns: 1fr;
    }
  }

  @media (max-width: 600px) {
    .sv-proof {
      padding: 56px 20px 64px;
    }

    .sv-proof-card {
      padding: 28px 24px;
    }

    .sv-proof-stat {
      padding: 24px;
    }

    .sv-proof-stat-value {
      font-size: 32px;
    }
  }
`;

function BodyWithEmphasis({ text, emphasis }: { text: string; emphasis: string }) {
  if (!emphasis) return <>{text}</>;

  const index = text.indexOf(emphasis);
  if (index === -1) return <>{text}</>;

  return (
    <>
      {text.slice(0, index)}
      <strong>{emphasis}</strong>
      {text.slice(index + emphasis.length)}
    </>
  );
}

function ManuscriptIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none">
      <path
        d="M5 4h11a3 3 0 013 3v12a2 2 0 01-2 2H8a3 3 0 01-3-3V4z"
        stroke="currentColor"
        strokeWidth={1.6}
        strokeLinejoin="round"
      />
      <path
        d="M5 4v13a3 3 0 003 3"
        stroke="currentColor"
        strokeWidth={1.6}
        strokeLinecap="round"
      />
      <path d="M9 8h6M9 12h6M9 16h4" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" />
    </svg>
  );
}

function StackedManuscriptsIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none">
      <path
        d="M3 7h18v2H3zM3 11h18v2H3zM3 15h18v2H3z"
        stroke="currentColor"
        strokeWidth={1.6}
        strokeLinejoin="round"
      />
      <circle cx={7} cy={8} r={0.8} fill="currentColor" />
      <circle cx={7} cy={12} r={0.8} fill="currentColor" />
      <circle cx={7} cy={16} r={0.8} fill="currentColor" />
    </svg>
  );
}

function OpenBookIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none">
      <path
        d="M3 5h7a2 2 0 012 2v13a2 2 0 00-2-2H3V5zM21 5h-7a2 2 0 00-2 2v13a2 2 0 012-2h7V5z"
        stroke="currentColor"
        strokeWidth={1.6}
        strokeLinejoin="round"
      />
      <path
        d="M6 9h4M6 12h4M14 9h4M14 12h4"
        stroke="currentColor"
        strokeWidth={1.4}
        strokeLinecap="round"
      />
    </svg>
  );
}

export default function HomeProofSection() {
  const { t, i18n } = useTranslation('main');
  const currentLanguage = i18n.resolvedLanguage || i18n.language;
  const legacy = (key: string, defaultValue: string) => String(t(`main.${key}`, { defaultValue }));
  const tm = (key: string, defaultValue: string) => {
    const sourceProofKey = `main.source_proof.${key}`;
    const localizedValue = i18n.getResource(currentLanguage, 'main', sourceProofKey);

    return typeof localizedValue === 'string' ? localizedValue : defaultValue;
  };

  const stats: ProofStat[] = [
    {
      value: tm('stats.manuscripts.value', '15,000'),
      unit: tm('stats.manuscripts.unit', 'manuscripts'),
      label: tm('stats.manuscripts.label', 'Handpicked classical texts, curated by Sanskrit scholars.'),
    },
    {
      value: tm('stats.words.value', '350M'),
      unit: tm('stats.words.unit', 'words'),
      label: tm('stats.words.label', 'Indexed across primary texts, commentaries, and sub-commentaries.'),
    },
    {
      value: tm('stats.excerpts.value', '2M'),
      unit: tm('stats.excerpts.unit', 'indexed excerpts'),
      label: tm('stats.excerpts.label', 'Across every classical discipline — medicine, grammar, music, astronomy, and beyond.'),
    },
  ];

  const cards: ProofCard[] = [
    {
      icon: <ManuscriptIcon />,
      title: tm('cards.authenticity.title', legacy('feature_authenticity_title', 'Unparalleled Authenticity')),
      body: tm(
        'cards.authenticity.body',
        legacy(
          'feature_authenticity_description',
          'Handpicked to preserve the original śāstric meaning across 350 million words. No modern reinterpretations, no Wikipedia summarization — just the classical corpus as the tradition preserved it.',
        ),
      ),
      bodyEmphasis: tm('cards.authenticity.emphasis', 'śāstric meaning'),
      proofFooter: tm('cards.authenticity.footer', 'Sourced from temple archives & private collections'),
      accent: 'blue',
    },
    {
      icon: <StackedManuscriptsIcon />,
      title: tm('cards.tradition.title', legacy('feature_truth_title', 'Untouched Tradition')),
      body: tm(
        'cards.tradition.body',
        legacy(
          'feature_truth_description',
          'Exclusive access to 15,000+ forgotten works — untouched by modern commentary, preserved in original Sanskrit, Tamil, Telugu, and regional scripts with their context intact.',
        ),
      ),
      bodyEmphasis: tm('cards.tradition.emphasis', '15,000+ forgotten works'),
      proofFooter: tm('cards.tradition.footer', 'Works unavailable in any other AI system'),
      accent: 'saffron',
    },
    {
      icon: <OpenBookIcon />,
      title: tm('cards.research.title', legacy('feature_research_title', 'Accelerated Research')),
      body: tm(
        'cards.research.body',
        legacy(
          'feature_research_description',
          'What takes scholars months, Sarvalok delivers in minutes. Cross-reference across texts, compare commentaries side by side, and trace ideas through centuries of tradition — in a single query.',
        ),
      ),
      bodyEmphasis: tm('cards.research.emphasis', 'Cross-reference across texts'),
      proofFooter: tm('cards.research.footer', 'One query surfaces what manual research takes weeks to find'),
      accent: 'blue',
    },
  ];

  return (
    <div className="bg-white">
      <style>{proofStyles}</style>
      <section className="sv-proof" aria-labelledby="home-proof-heading">
        <div className="sv-proof-header">
          <span className="sv-proof-eyebrow">{tm('eyebrow', 'Why Sarvalok')}</span>
          <h2 id="home-proof-heading">{tm('heading', 'The difference lives in the source.')}</h2>
          <p>
            {tm(
              'subheading',
              'Every answer comes from a curated archive of classical Indian texts — not the open internet, not AI-generated summaries, not Wikipedia-tier paraphrase.',
            )}
          </p>
        </div>

        <div className="sv-proof-stats" role="list">
          {stats.map((stat) => (
            <div key={stat.unit} className="sv-proof-stat" role="listitem">
              <p className="sv-proof-stat-value">
                {stat.value}
                <span className="sv-proof-stat-unit"> {stat.unit}</span>
              </p>
              <p className="sv-proof-stat-label">{stat.label}</p>
            </div>
          ))}
        </div>

        <div className="sv-proof-cards">
          {cards.map((card) => (
            <article key={card.title} className="sv-proof-card">
              <div className="sv-proof-icon" data-accent={card.accent} aria-hidden="true">
                {card.icon}
              </div>

              <h3>{card.title}</h3>

              <p className="sv-proof-body">
                <BodyWithEmphasis text={card.body} emphasis={card.bodyEmphasis} />
              </p>

              <div className="sv-proof-card-footer">
                <span className="sv-proof-card-dot" data-accent={card.accent} aria-hidden="true" />
                {card.proofFooter}
              </div>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}
